import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PatPaymentConfirmationPageConfig } from '@front-platform/content-builder';
import { patPaymentConfirmationPageResolveConfig } from '@front-platform/content-builder';
import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, concat, interval, of, Subject, timer } from 'rxjs';
import {
  switchMap,
  exhaustMap,
  take,
  takeUntil,
  takeWhile,
  catchError,
  filter,
  tap,
} from 'rxjs/operators';
import { DoodOpenCheckTransaction, DoodCheckTransactionStatus } from '@core/models/check.model';
import { IContentBuilderFieldImage } from '@core/models/content-builder-fields.model';

import { Paths } from 'src/app/config/paths.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { PaymentAtTableStoreSelector } from '@common/selectors/payment-at-table.selector';
import { PaymentAtTableStoreDispatcher } from '@common/dispatchers/payment-at-table.dispatcher';
import { CheckTransactionKeys } from '@config/keys/check.keys';
import { ModalsService } from '@core/services/modals/modals.service';
import {
  ReceiveReceiptModalComponent,
  ReceiveReceiptModalReceiptType,
} from '@shared/modals/receive-receipt-modal/receive-receipt-modal.component';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { isPatMockMode, resolvePatMockCheckId } from '@config/values/pat.values';
export interface PatPaymentConfirmationPageRuntimeConfig {
  'body'?: any;
}

export type PatPaymentConfirmationPageConfigInput = Partial<
  Omit<PatPaymentConfirmationPageConfig, keyof PatPaymentConfirmationPageRuntimeConfig> &
    PatPaymentConfirmationPageRuntimeConfig
>;
export type PatPaymentConfirmationPageResolvedConfig = Omit<
  PatPaymentConfirmationPageConfig,
  keyof PatPaymentConfirmationPageRuntimeConfig
> &
  Partial<PatPaymentConfirmationPageRuntimeConfig>;
export interface PatPaymentConfirmationPageContract extends ConfigComponentContract<
  PatPaymentConfirmationPageConfigInput,
  PatPaymentConfirmationPageResolvedConfig
> {}

@Component({
  selector: 'app-pat-payment-confirmation-page',
  templateUrl: './pat-payment-confirmation-page.component.html',
  styleUrls: ['./pat-payment-confirmation-page.component.scss'],
  standalone: false,
})
export class PatPaymentConfirmationPageComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, PatPaymentConfirmationPageContract
{
  readonly config = input<PatPaymentConfirmationPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PatPaymentConfirmationPageConfig,
    PatPaymentConfirmationPageRuntimeConfig
  >(patPaymentConfirmationPageResolveConfig, () => ({ config: this.config() }));
  get body(): any {
    return this.resolvedConfig()['body'] as unknown as any;
  }

  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }

  isLoading = false;
  outstandingAmount$ = this.paymentAtTableSelector.selectCheckOutstandingAmount;
  isTransactionCancelled = false;
  transaction!: DoodOpenCheckTransaction;

  // Progressive message states
  showLongerMessage = false;
  showReferenceMessage = false;
  showIssueMessage = false;
  transactionId: string | null = null;

  // Animation states
  showSecondStep = false;

  private previousDocumentTitle = '';
  private isMockMode = false;
  /** Time the loading screen stayed in the foreground. Background tabs do not count. */
  private visibleLoadingMs = 0;
  private userWasBlocked = false;
  private lastBlockedReportVisibleMs = 0;
  private static readonly blockedAfterMs = 120_000;
  private static readonly blockedReportEveryMs = 60_000;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly title: Title,
    private readonly translate: TranslateService,
    private readonly routerHelper: RouterHelperService,
    private readonly paymentAtTableSelector: PaymentAtTableStoreSelector,
    private readonly paymentAtTableDispatcher: PaymentAtTableStoreDispatcher,
    private readonly onSiteLocationsService: OnSiteLocationsService,
    private readonly modalsService: ModalsService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.transactionId = this.activatedRoute.snapshot.paramMap.get('id');
    this.isMockMode = isPatMockMode(resolvePatMockCheckId(this.activatedRoute.snapshot));

    this.previousDocumentTitle = this.title.getTitle();
    this.title.setTitle(
      this.translate.instant('pay-at-table.payment-confirmation.payment-confirmed'),
    );

    this.analyticsService.log({
      level: 'debug',
      label: 'PatPaymentConfirmationPage - OnInit',
      message: {
        from: 'PatPaymentConfirmationPageComponent ngOnInit',
        target: this.isMockMode ? 'applyMockTransaction' : 'getOnSiteLocationCheckTransactions$',
        currentLocation: window.location.pathname,
        params: this.activatedRoute.snapshot.params,
        queryParams: this.activatedRoute.snapshot.queryParams,
      },
    });

    this.setupProgressiveMessages();
    this.watchBlockedUser();
    this.setupStepAnimation();

    if (this.isMockMode) {
      this.applyMockTransaction();
      this.paymentAtTableSelector.selectCheck
        .pipe(
          takeUntil(this._destroyerRef),
          filter(check => (check.transactions?.length ?? 0) > 0),
          tap(() => this.applyMockTransaction()),
        )
        .subscribe();
    } else {
      this.getOnSiteLocationCheckTransactions$();
    }
  }

  ngOnDestroy(): void {
    if (this.userWasBlocked && this.isLoading) {
      this.logBlockedState(
        'PatPaymentConfirmationPage - UserLeftWhileBlocked',
        'warn',
        'User left the payment confirmation page while the loading screen was still showing',
      );
      this.userWasBlocked = false;
    }
    this.title.setTitle(this.previousDocumentTitle);
    super.ngOnDestroy();
  }

  goToOverview(): void {
    this.router.navigate([
      this.routerHelper.translateRoute(`/${Paths.PayAtTable}/${Paths.PayAtTableOverview}`),
    ]);
  }

  openModalReceipt(): void {
    this.modalsService.open(ReceiveReceiptModalComponent.handle, undefined, {
      receiptType: ReceiveReceiptModalReceiptType.checkTransaction,
      receiptId: this.transaction[CheckTransactionKeys.Id],
    });
  }

  /**
   * Reports a user who is still looking at the loading screen after two minutes,
   * then again every minute until that screen goes away.
   */
  private watchBlockedUser(): void {
    let lastTick = Date.now();

    interval(5000)
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(() => {
        const now = Date.now();
        const delta = now - lastTick;
        lastTick = now;

        if (!this.isLoading) {
          this.reportUserUnblocked();
          this.visibleLoadingMs = 0;
          this.lastBlockedReportVisibleMs = 0;
          return;
        }

        if (document.visibilityState !== 'visible') {
          return;
        }

        this.visibleLoadingMs += delta;
        const stillBlocked =
          this.visibleLoadingMs >= PatPaymentConfirmationPageComponent.blockedAfterMs &&
          this.visibleLoadingMs - this.lastBlockedReportVisibleMs >=
            PatPaymentConfirmationPageComponent.blockedReportEveryMs;

        if (!stillBlocked) {
          return;
        }

        this.lastBlockedReportVisibleMs = this.visibleLoadingMs;
        this.userWasBlocked = true;
        this.logBlockedState(
          'PatPaymentConfirmationPage - UserBlocked',
          'warn',
          'User is still on the payment confirmation loading screen',
        );
      });
  }

  private reportUserUnblocked(): void {
    if (!this.userWasBlocked) {
      return;
    }
    this.userWasBlocked = false;
    this.logBlockedState(
      'PatPaymentConfirmationPage - UserUnblocked',
      'info',
      'Payment confirmation loading screen closed after the user was blocked',
    );
  }

  private logBlockedState(
    label:
      | 'PatPaymentConfirmationPage - UserBlocked'
      | 'PatPaymentConfirmationPage - UserUnblocked'
      | 'PatPaymentConfirmationPage - UserLeftWhileBlocked',
    level: 'warn' | 'info',
    target: string,
  ): void {
    this.analyticsService.log({
      level,
      label,
      includeStaging: true,
      message: {
        from: 'PatPaymentConfirmationPageComponent watchBlockedUser',
        target,
        currentLocation: window.location.pathname,
        transactionId: this.transactionId,
        visibleLoadingSeconds: Math.round(this.visibleLoadingMs / 1000),
        visibilityState: document.visibilityState,
        isLoading: this.isLoading,
        isTransactionCancelled: this.isTransactionCancelled,
      },
    });
  }

  private setupProgressiveMessages(): void {
    // Show "operation taking longer" message after 20 seconds
    setTimeout(() => {
      if (this.isLoading) {
        this.showLongerMessage = true;
        this.analyticsService.log({
          level: 'debug',
          label: 'PatPaymentConfirmationPage - ShowLongerMessage',
          message: {
            from: 'PatPaymentConfirmationPageComponent setupProgressiveMessages',
            target: 'Show longer message after 20s',
            currentLocation: window.location.pathname,
            transactionId: this.transactionId,
          },
        });
      }
    }, 20000);

    // Show reference message after 30 seconds
    setTimeout(() => {
      if (this.isLoading) {
        this.showReferenceMessage = true;
        this.analyticsService.log({
          level: 'debug',
          label: 'PatPaymentConfirmationPage - ShowReferenceMessage',
          message: {
            from: 'PatPaymentConfirmationPageComponent setupProgressiveMessages',
            target: 'Show reference message after 30s',
            currentLocation: window.location.pathname,
            transactionId: this.transactionId,
          },
        });
      }
    }, 30000);

    // Replace longer message with issue message after 60 seconds
    setTimeout(() => {
      if (this.isLoading) {
        this.showIssueMessage = true;
        this.showLongerMessage = false; // Replace the previous message
        this.analyticsService.log({
          level: 'debug',
          label: 'PatPaymentConfirmationPage - ShowIssueMessage',
          message: {
            from: 'PatPaymentConfirmationPageComponent setupProgressiveMessages',
            target: 'Show issue message after 60s',
            currentLocation: window.location.pathname,
            transactionId: this.transactionId,
          },
        });
      }
    }, 60000);
  }

  private getOnSiteLocationCheckTransactions$(): void {
    this.isLoading = true;
    const stopPolling$ = new Subject<boolean>();

    this.analyticsService.log({
      level: 'debug',
      label: 'PatPaymentConfirmationPage - StartPolling',
      message: {
        from: 'PatPaymentConfirmationPageComponent getOnSiteLocationCheckTransactions$',
        target: 'Start progressive polling for transaction status',
        currentLocation: window.location.pathname,
        transactionId: this.activatedRoute.snapshot.paramMap.get('id'),
        isLoading: this.isLoading,
      },
    });

    // First poll after 2s, then every 5s (10x), then every 10s (unlimited)
    const mediumPolling$ = timer(2000, 5000).pipe(take(10));
    const slowPolling$ = interval(10000);

    concat(mediumPolling$, slowPolling$)
      .pipe(
        takeUntil(stopPolling$),
        exhaustMap((_tick, pollIndex) => {
          const pollingPhase = pollIndex < 10 ? 'medium' : 'slow';

          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - Polling',
            message: {
              from: 'PatPaymentConfirmationPageComponent progressive polling',
              target: `Poll attempt ${pollIndex} (${pollingPhase} phase)`,
              currentLocation: window.location.pathname,
              isLoading: this.isLoading,
              pollCount: pollIndex,
              pollingPhase,
            },
          });

          return this.paymentAtTableSelector.selectCheckActiveTransaction.pipe(
            take(1),
            switchMap(activeTransaction => {
              this.analyticsService.log({
                level: 'debug',
                label: 'PatPaymentConfirmationPage - ActiveTransaction',
                message: {
                  from: 'PatPaymentConfirmationPageComponent activeTransaction check',
                  target: 'Get transaction from store or API',
                  currentLocation: window.location.pathname,
                  activeTransaction: activeTransaction ? 'exists' : 'null',
                  activeTransactionId: activeTransaction?.id,
                },
              });

              if (!activeTransaction) {
                const id = this.activatedRoute.snapshot.paramMap.get('id');

                this.analyticsService.log({
                  level: 'debug',
                  label: 'PatPaymentConfirmationPage - NoActiveTransaction',
                  message: {
                    from: 'PatPaymentConfirmationPageComponent no active transaction',
                    target: 'Use ID from URL params',
                    currentLocation: window.location.pathname,
                    urlParamId: id,
                    willCallAPI: !!id,
                  },
                });

                return id
                  ? this.onSiteLocationsService.getOnSiteLocationCheckTransactions$(id)
                  : of(null);
              }

              this.analyticsService.log({
                level: 'debug',
                label: 'PatPaymentConfirmationPage - UseActiveTransaction',
                message: {
                  from: 'PatPaymentConfirmationPageComponent using active transaction',
                  target: 'Get transaction from API',
                  currentLocation: window.location.pathname,
                  activeTransactionId: activeTransaction.id,
                },
              });

              return this.onSiteLocationsService.getOnSiteLocationCheckTransactions$(
                activeTransaction.id,
              );
            }),
            catchError(err => {
              this.analyticsService.log({
                level: 'error',
                label: 'PatPaymentConfirmationPage - APIError',
                message: {
                  from: 'PatPaymentConfirmationPageComponent API error',
                  target: 'Error getting transaction from API',
                  currentLocation: window.location.pathname,
                  error: err?.message || err?.detail || 'Unknown error',
                  errorCode: err?.status || 'N/A',
                  isLoading: this.isLoading,
                },
              });

              // Set isLoading to false on error to prevent infinite loading
              this.isLoading = false;
              return of(null);
            }),
            switchMap(transaction => {
              this.analyticsService.log({
                level: 'debug',
                label: 'PatPaymentConfirmationPage - TransactionReceived',
                message: {
                  from: 'PatPaymentConfirmationPageComponent transaction received',
                  target: 'Process transaction status',
                  currentLocation: window.location.pathname,
                  transactionExists: !!transaction,
                  transactionStatus: transaction?.status || 'null',
                  transactionId: transaction?.id,
                  isLoading: this.isLoading,
                },
              });

              if (transaction && transaction.status === 'CAPTURED') {
                this.analyticsService.log({
                  level: 'debug',
                  label: 'PatPaymentConfirmationPage - TransactionCaptured',
                  message: {
                    from: 'PatPaymentConfirmationPageComponent transaction captured',
                    target: 'Set isLoading to false and get check details',
                    currentLocation: window.location.pathname,
                    transactionId: transaction.id,
                    transactionStatus: transaction.status,
                    isLoading: this.isLoading,
                  },
                });

                this.transaction = transaction;
                const checkId = this.paymentAtTableSelector.check.id;

                this.analyticsService.log({
                  level: 'debug',
                  label: 'PatPaymentConfirmationPage - CheckId',
                  message: {
                    from: 'PatPaymentConfirmationPageComponent getting check ID',
                    target: 'Check ID from store',
                    currentLocation: window.location.pathname,
                    checkId: checkId || 'null',
                    willCallCheckAPI: !!checkId,
                  },
                });

                this.resetPatChooseState();
                this.resetProgressiveMessages();

                this.isLoading = false;
                stopPolling$.next(true);
                stopPolling$.complete();
                return combineLatest([
                  of(transaction),
                  this.onSiteLocationsService.getOnSiteLocationCheck$(checkId),
                ]);
              }

              if (transaction && transaction.status === 'CANCELLED') {
                this.analyticsService.log({
                  level: 'debug',
                  label: 'PatPaymentConfirmationPage - TransactionCancelled',
                  message: {
                    from: 'PatPaymentConfirmationPageComponent transaction cancelled',
                    target: 'Stop polling and set cancelled flag',
                    currentLocation: window.location.pathname,
                    transactionId: transaction.id,
                    transactionStatus: transaction.status,
                    isLoading: this.isLoading,
                  },
                });

                this.isTransactionCancelled = true;
                this.resetProgressiveMessages();
                this.isLoading = false;
                stopPolling$.next(true);
                stopPolling$.complete();
              }

              if (!transaction) {
                this.analyticsService.log({
                  level: 'debug',
                  label: 'PatPaymentConfirmationPage - NoTransaction',
                  message: {
                    from: 'PatPaymentConfirmationPageComponent no transaction',
                    target: 'No transaction returned from API',
                    currentLocation: window.location.pathname,
                    isLoading: this.isLoading,
                  },
                });
              }

              return combineLatest([of(transaction), of(null)]);
            }),
          );
        }),
        takeUntil(this._destroyerRef),
        takeWhile(() => {
          const shouldContinue = this.isLoading;

          // Log polling continuation decision
          if (!shouldContinue) {
            this.analyticsService.log({
              level: 'debug',
              label: 'PatPaymentConfirmationPage - PollingStopped',
              message: {
                from: 'PatPaymentConfirmationPageComponent polling stopped',
                target: 'isLoading is false, stopping polling',
                currentLocation: window.location.pathname,
                isLoading: this.isLoading,
              },
            });
          }

          return shouldContinue;
        }, true),
      )
      .subscribe({
        next: result => {
          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - PollingResult',
            message: {
              from: 'PatPaymentConfirmationPageComponent polling result',
              target: 'Polling subscription result',
              currentLocation: window.location.pathname,
              result: result ? 'success' : 'null',
              isLoading: this.isLoading,
            },
          });
        },
        error: err => {
          this.analyticsService.log({
            level: 'error',
            label: 'PatPaymentConfirmationPage - PollingError',
            message: {
              from: 'PatPaymentConfirmationPageComponent polling error',
              target: 'Polling subscription error',
              currentLocation: window.location.pathname,
              error: err?.message || 'Unknown polling error',
              isLoading: this.isLoading,
            },
          });

          // Set isLoading to false on subscription error
          this.isLoading = false;
        },
        complete: () => {
          this.analyticsService.log({
            level: 'debug',
            label: 'PatPaymentConfirmationPage - PollingComplete',
            message: {
              from: 'PatPaymentConfirmationPageComponent polling complete',
              target: 'Polling subscription completed',
              currentLocation: window.location.pathname,
              isLoading: this.isLoading,
            },
          });

          // Set isLoading to false when polling completes
          this.isLoading = false;
        },
      });
  }

  private resetPatChooseState(): void {
    this.analyticsService.log({
      level: 'debug',
      label: 'PatPaymentConfirmationPage - ResetState',
      message: {
        from: 'PatPaymentConfirmationPageComponent reset state',
        target: 'Reset PAT choose state',
        currentLocation: window.location.pathname,
        isLoading: this.isLoading,
      },
    });

    this.paymentAtTableDispatcher.resetChoose();
  }

  private setupStepAnimation(): void {
    // Show second step after 800ms with smooth animation
    setTimeout(() => {
      if (this.isLoading) {
        this.showSecondStep = true;
      }
    }, 800);
  }

  private resetProgressiveMessages(): void {
    this.showLongerMessage = false;
    this.showReferenceMessage = false;
    this.showIssueMessage = false;
  }

  private applyMockTransaction(): void {
    const routeTransactionId = this.transactionId;
    const check = this.paymentAtTableSelector.check;
    const transactions = check.transactions ?? [];

    const transaction =
      transactions.find(t => t[CheckTransactionKeys.Id] === routeTransactionId) ??
      check.active_transaction ??
      transactions[0] ??
      null;

    if (!transaction) {
      this.isLoading = true;
      return;
    }

    this.transaction = transaction;

    if (transaction.status === DoodCheckTransactionStatus.Captured) {
      this.isLoading = false;
      this.resetPatChooseState();
      this.resetProgressiveMessages();
      return;
    }

    if (transaction.status === DoodCheckTransactionStatus.Cancelled) {
      this.isTransactionCancelled = true;
      this.isLoading = false;
      this.resetProgressiveMessages();
      return;
    }

    // AUTHORIZED / PAYMENT — keep loading state for progressive message testing
    this.isLoading = true;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PatPaymentConfirmationPageComponent, {
  'keyAliases': {},
});
